import {
  Box,
  Divider,
  Text,
  Link,
  Space,
  Flex,
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalContent,
  Modal,
  ModalOverlay,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import { AuthenticationInfoRes, RejectProvisionReason } from '@Types/provision';
import { Trans, useTranslation } from 'react-i18next';
import {
  AppInfoForm,
  ProvisionOrgInfo,
  StepIcon,
  UserIcon,
} from '../../Dashboard/ProvisionModal/share';
import { Steps } from 'antd';
import { ProvisionApi } from '@Apis';
import { useEffect, useRef, useState } from 'react';
import RejectProvisionModal from '../../Dashboard/ProvisionModal/rejectProvisionModal';
import ErrorAlert from '@Components/Common/ErrorAlert';
import i18next from 'i18next';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import CSpinner from '@Components/CSpinner';
import { Service } from '@Types/app';
const TeamsChatUpdateSecretOrChangeAppModal = ({
  appId,
  orgId,
  onClose,
}: {
  appId: string;
  orgId: string;
  onClose: () => void;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['provision', 'common']);
  //0: not start, 1: done
  const [provisionStatus, setProvisionStatus] = useState(0);
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const formRef = useRef<any>(null);
  const timeRef = useRef<any>(null);

  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);
  const [loading, toggleLoading] = useToggle(false);

  useEffect(() => {
    ProvisionApi.apiGetTeamsChatRenewAuthentications(orgId)
      .then((res) => {
        setAuthenticationInfo(res.data);
      })
      .catch((e: any) => {
        console.error(JSON.stringify(e));
        const errorCode = e.data?.error?.details?.[0].errorCode;
        if (
          errorCode === RejectProvisionReason.ServiceConflict ||
          errorCode === RejectProvisionReason.PocConflict
        ) {
          setRejectReason(errorCode);
        }
      });
  }, [orgId]);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={Service.TeamsChat}
        reason={rejectReason}
      />
    );
  }

  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    let formData: any = {};
    try {
      formData = formRef.current?.getData();
      console.log(formData);
    } catch (error) {
      console.error(error);
      return;
    }
    setErrorMsg(undefined);
    toggleLoading(true);
    window.open(authUrl + `&client_id=${formData.appId}`, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const teamsChatProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_teams_chat'
      );
      if (teamsChatProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(teamsChatProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiRenewTeamsChat(
              code,
              formData.paymentModel,
              formData.appId,
              formData.appSecret,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.teamsChat.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setProvisionStatus(1);
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.teamsChat.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  const titleTextStyle = {
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: '400',
    color: colorStyle.color.primary,
  };

  return (
    <>
      {loading && <CSpinner />}
      <Modal isOpen={true} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('administration.sa.teamsChatUpdateApp')}</ModalHeader>
          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />
            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex direction="column" gap="16px">
              <Steps
                direction="vertical"
                current={provisionStatus + 1}
                items={[
                  {
                    icon: <UserIcon />,
                    title: (
                      <Text {...titleTextStyle}>
                        <Trans
                          i18nKey="provision.service.teamsChat.installApp"
                          ns="provision"
                        >
                          placeholder
                          <Link
                            href={getCasOLHLink(
                              CAS_DOC_LINK.provision.teamsChat.instruction,
                              i18next.language
                            )}
                            target="_blank"
                          >
                            placeholder
                          </Link>
                          placeholder
                        </Trans>
                      </Text>
                    ),
                    description: <Space height="16px" />,
                  },
                  {
                    icon: <StepIcon checked={provisionStatus === 1} />,
                    title: (
                      <Text pt="4px" {...titleTextStyle}>
                        {t('provision.service.teamsChat.provideAppInfo')}
                      </Text>
                    ),
                    description:
                      provisionStatus === 0 ? (
                        <Flex direction="column" pt="4px" gap="16px">
                          <AppInfoForm ref={formRef} appId={appId} />
                          <Divider variant="dashed" orientation="horizontal" />
                          <Box>
                            <Button
                              variant="secondary"
                              borderRadius="2rem"
                              onClick={onClick}
                            >
                              {t('provision.button.grantPermission')}
                            </Button>
                          </Box>
                        </Flex>
                      ) : (
                        <Text
                          color={colorStyle.color.tertiary}
                          pt="8px"
                          lineHeight="20px"
                        >
                          {t('provision.common.permissionGranted')}
                        </Text>
                      ),
                  },
                ]}
              />
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
export default TeamsChatUpdateSecretOrChangeAppModal;
